import { Component, OnInit } from '@angular/core';
import { Sort } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { QuestionnaireType } from 'src/API';
import { PatientService } from 'src/app/core/api/patient.service';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { QuestionnaireStatusPipe } from 'src/app/shared/shared-pipes/questionnaire-status.pipe';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from './../../../core/api/questionnaire-request.service';
import { QuestionnaireResponseModalComponent } from './../../../shared/consult-forms/questionnaire-response-modal/questionnaire-response-modal.component';
import { QrCodeModalComponent } from './../../../shared/qr-code-modal/qr-code-modal.component';
import { LinkQuestionnaireToAssessmentModalComponent } from './../../link-questionnaire-to-assessment-modal/link-questionnaire-to-assessment-modal.component';

@Component({
  selector: 'csi-questionnaires-table',
  templateUrl: './questionnaires-table.component.html',
  styleUrls: ['./questionnaires-table.component.css'],
  providers: [QuestionnaireStatusPipe, QuestionnaireScorePipe]
})
export class QuestionnairesTableComponent implements OnInit {
  // canonical questionnaire identifiers
  private questionnaireCanonicalMap: { [key: string]: string } = {
    // DEQ
    deq: 'DEQ',
    'dry eye questionnaire': 'DEQ',

    // OSDI 12
    osdi: 'OSDI_12',
    osdi12: 'OSDI_12',
    'osdi 12': 'OSDI_12',

    // OSDI 6, no number in value so that OSDI 6 sorts before OSDI 12
    osdi6: 'OSDI',
    'osdi 6': 'OSDI',

    // SPEED
    speed: 'SPEED',
    speedii: 'SPEED_II',

    // Combined
    osdiandspeed: 'OSDI_AND_SPEED',

    // Others
    cderfs: 'CDERFS',
    derfs: 'DERFS',
    medications_questionnaire: 'MEDICATIONS',
    standard_intakeform: 'STANDARD_INTAKE',
    fitzpatrick: 'FITZPATRICK'
  };

  public QuestionnaireType = QuestionnaireType;
  public readonly translocoFileKey = 'dashboard.patients-dashboard.';
  public questionnaireTypeToFriendlyNameMap: { [key in QuestionnaireType]?: string } = {
    [QuestionnaireType.DEQ]: 'DEQ',
    [QuestionnaireType.OSDI]: 'OSDI 12',
    [QuestionnaireType.SPEED]: 'SPEED',
    [QuestionnaireType.SPEEDII]: 'SPEED II',
    [QuestionnaireType.OSDIAndSPEED]: 'OSDI 12 and SPEED',
    [QuestionnaireType.DERFS]: 'DERFS',
    [QuestionnaireType.FITZPATRICK]: 'FITZPATRICK',
    [QuestionnaireType.CDERFS]: 'CDERFS',
    [QuestionnaireType.OSDI6]: 'OSDI 6',
    [QuestionnaireType.MEDICATION]: 'MEDICATION'
  };

  public readonly questionnaireHeaderMap = {
    createdAt: 'Sent On',
    type: 'Type',
    completedAt: 'Completed At',
    status: 'Status',
    score: 'Score',
    assessment: 'Linked Assessment Date'
  };

  public readonly questionnaireHeaderKeysWithoutActionKey = Object.keys(
    this.questionnaireHeaderMap
  );
  public readonly questionnaireHeaderKeysWithActionsKey = [
    ...this.questionnaireHeaderKeysWithoutActionKey,
    'actions'
  ];

  constructor(
    public router: Router,
    private dialog: MatDialog,
    public questionnaireRequestService: QuestionnaireRequestService,
    public patientService: PatientService,
    private questionnaireStatusPipe: QuestionnaireStatusPipe,
    private questionnaireScorePipe: QuestionnaireScorePipe,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.patientService.questionnaires = this.patientService.questionnaires.map(questionnaire => ({
      ...questionnaire,
      config:
        (questionnaire.config as any) instanceof Object
          ? questionnaire.config
          : JSON.parse(questionnaire.config)
    }));
  }

  showQuestionnaireResponse(questionnaire: QuestionnaireRequest) {
    QuestionnaireResponseModalComponent.open(
      this.dialog,
      questionnaire,
      null,
      this.patientService.patient.id
    );
  }

  openQRCode(questionnaire: QuestionnaireRequest) {
    const path = `${location.origin}/questionnaires/${questionnaire.id}`;
    const questionnaireType: string = this.questionnaireRequestService
      .questionnaireTypeToFriendlyNameMap[questionnaire.type];

    const scanTitle = this.translocoService.translate('Scan the QR Code to open the');
    const questionnaireLabel = this.translocoService.translate('questionnaire');

    this.dialog.open(QrCodeModalComponent, {
      data: {
        qrCodePath: path,
        modalTitle: scanTitle + ' ' + questionnaireType + ' ' + questionnaireLabel
      }
    });
  }

  openLinkQuestionnaireToAssessmentModal(questionnaire: QuestionnaireRequest) {
    this.dialog.open(LinkQuestionnaireToAssessmentModalComponent, {
      data: {
        questionnaire
      },
      width: '80vw'
    });
  }

  private getScoreRank(questionnaire: any): number {
    // 1 = Not At Risk
    // 2 = At Risk
    // 3 = N/A

    if (!questionnaire) {
      return 3;
    }

    // These NEVER have at risk
    if (
      questionnaire.type === 'CUSTOM' ||
      questionnaire.type === 'MEDICATION' ||
      questionnaire.type === 'DERFS' ||
      questionnaire.type === 'CDERFS'
    ) {
      return 3;
    }

    if (!questionnaire.response || !questionnaire.response['scores']) {
      return 3;
    }

    try {
      const scores =
        typeof questionnaire.response['scores'] === 'string'
          ? questionnaire.response['scores']
          : JSON.stringify(questionnaire.response['scores']);

      if (scores === '{}' || scores === '[]') {
        return 3;
      }

      if (this.questionnaireRequestService.isAtRisk(scores)) {
        return 2;
      }

      return 1;
    } catch (error) {
      return 3;
    }
  }

  private getCanonicalQuestionnaireKey(questionnaire: any): string {
    var raw: string;

    if (
      questionnaire &&
      questionnaire.config &&
      questionnaire.config.schema &&
      questionnaire.config.schema.id
    ) {
      raw = questionnaire.config.schema.id;
    } else if (questionnaire && questionnaire.type) {
      raw = questionnaire.type;
    } else {
      return 'ZZZ';
    }

    raw = raw.toLowerCase();

    return this.questionnaireCanonicalMap[raw] || raw.toUpperCase();
  }

  announceSortChange(sort: Sort) {
    this.patientService.questionnaires = [
      ...this.patientService.questionnaires.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        switch (sort.active) {
          case 'createdAt':
            return this.compare(
              new Date(a.createdAt).getTime(),
              new Date(b.createdAt).getTime(),
              isAsc
            );
          case 'type': {
            var keyA = this.getCanonicalQuestionnaireKey(a);
            var keyB = this.getCanonicalQuestionnaireKey(b);

            return this.compare(keyA, keyB, isAsc);
          }
          case 'completedAt':
            return this.compare(
              new Date(a.completedAt).getTime(),
              new Date(b.completedAt).getTime(),
              isAsc
            );
          case 'assessment':
            return this.compare(
              a.assessment && new Date(a.assessment.createdAt).getTime(),
              b.assessment && new Date(b.assessment.createdAt).getTime(),
              isAsc
            );
          case 'status':
            const questionnaireStatusA = this.questionnaireStatusPipe.transform(a);
            const questionnaireStatusB = this.questionnaireStatusPipe.transform(b);
            return this.compare(questionnaireStatusA, questionnaireStatusB, isAsc);
          case 'score': {
            const rankA = this.getScoreRank(a);
            const rankB = this.getScoreRank(b);

            return this.compare(rankA, rankB, isAsc);
          }
        }
      })
    ];
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    if (a === b) {
      return 0;
    }

    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
